import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Swords, Trophy, Users } from "lucide-react";
import { GAMES, GameBg, GameBadge } from "../components/WagerPicker";
import SiteFooter from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERKS = [
  { Icon: Trophy, text: "Be first to play when we launch" },
  { Icon: Users, text: "Challenge your mates on day one" },
  { Icon: Swords, text: "Founding player badge on your profile" },
];

const Field = ({ id, label, ...p }) => (
  <label className="block">
    <span className="text-sm font-semibold text-[#0F172A]">{label}</span>
    <input id={id} data-testid={`interest-${id}-input`} required {...p}
      className="mt-1.5 w-full rounded-xl border border-[#CBD5E1] bg-white px-4 py-3 text-[15px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/25" />
  </label>
);

const InterestForm = ({ source }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/interest`, { ...f, source });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? "Please check your details: a valid email and phone number are needed." : d || "Something went wrong, try again");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div data-testid="interest-success" className="text-center py-6">
        <CheckCircle2 className="w-14 h-14 text-[#10B981] mx-auto" />
        <h2 className="font-display font-extrabold uppercase text-3xl text-[#0F172A] mt-3">You're on the list</h2>
        <p className="text-[#475569] mt-2">Thanks {f.name.split(" ")[0]}! We'll be in touch at {f.email} as soon as early access opens.</p>
        <Link to="/" data-testid="interest-play-link" className="btn-green inline-block rounded-xl px-6 py-3 mt-6 font-bold">Try the game now</Link>
      </div>
    );
  }

  return (
    <form data-testid="interest-form" onSubmit={submit} className="space-y-4">
      <Field id="name" label="Full name" placeholder="Jamie Carragher" value={f.name} onChange={set("name")} maxLength={80} autoComplete="name" />
      <Field id="email" label="Email" type="email" placeholder="you@example.com" value={f.email} onChange={set("email")} autoComplete="email" />
      <Field id="phone" label="Mobile number" type="tel" placeholder="07700 900123" value={f.phone} onChange={set("phone")} pattern="\+?[0-9 \(\)\-]{7,20}" title="7-20 digits, spaces, brackets or dashes" autoComplete="tel" />
      {error && <p data-testid="interest-error" className="text-sm text-[#DC2626]">{error}</p>}
      <button data-testid="interest-submit-btn" disabled={busy} className="btn-green w-full rounded-xl py-4 font-extrabold uppercase text-lg flex items-center justify-center gap-2 disabled:opacity-60">
        {busy && <Loader2 className="w-5 h-5 animate-spin" />} Get early access
      </button>
      <p className="text-xs text-[#64748B] leading-relaxed">By registering you agree we can contact you about Footie Clash. No spam, unsubscribe any time. See our <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
    </form>
  );
};

export default function RegisterInterest() {
  const params = new URLSearchParams(window.location.search);
  const source = params.get("utm_source") || params.get("ref") || params.get("source") || "direct";
  useEffect(() => { document.title = "Get early access · Footie Clash"; }, []);

  return (
    <div data-testid="register-interest-page" className="theme-light bg-app min-h-screen flex flex-col">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-extrabold uppercase text-xl text-[#0F172A]">
            <span className="w-8 h-8 rounded-md bg-[#10B981] flex items-center justify-center"><Swords className="w-4 h-4 text-[#0F172A]" strokeWidth={2.5} /></span>
            Footie Clash
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-[#059669] bg-[#10B981]/10 rounded-full px-3 py-1">Early access</span>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-5 lg:px-8 py-10 lg:py-16 grid lg:grid-cols-[1fr_440px] gap-10 lg:gap-16 items-start">
        <section className="fade-up">
          <div className="text-[12px] label-eyebrow text-[#059669]">Coming soon</div>
          <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-[#0F172A] mt-2">The football game you play with your mates</h1>
          <p className="text-[#475569] text-base lg:text-lg mt-5 max-w-xl leading-relaxed">Draft your dream XI, step up for penalties and settle it with your mates. Register now to get early access before anyone else.</p>
          <ul className="mt-7 space-y-3">
            {PERKS.map(({ Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-[#0F172A] font-medium">
                <span className="w-9 h-9 rounded-lg bg-[#10B981]/12 flex items-center justify-center"><Icon className="w-5 h-5 text-[#059669]" /></span>{text}
              </li>
            ))}
          </ul>
          <div className="mt-9 grid sm:grid-cols-2 gap-3 max-w-xl">
            {GAMES.map((g) => (
              <div key={g.id} className="on-dark relative overflow-hidden rounded-2xl px-4 py-5 text-white">
                <GameBg game={g} />
                <div className="relative flex items-center gap-3">
                  <GameBadge game={g} size="w-11 h-11" icon="w-6 h-6" />
                  <div>
                    <div className="font-display font-black uppercase text-2xl leading-none">{g.title}</div>
                    <div className="text-xs text-[#A7F3D0] mt-1">{g.sub}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8 shadow-[0_10px_40px_rgba(15,23,42,0.08)] fade-up">
          <h2 className="font-display font-extrabold uppercase text-3xl text-[#0F172A]">Register your interest</h2>
          <p className="text-sm text-[#64748B] mt-1 mb-6">Takes 20 seconds. Free.</p>
          <InterestForm source={source} />
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
